import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { environment } from 'src/environments/environment';
import Swal from 'sweetalert2';

@Component({
   selector: 'app-security',
   templateUrl: './security.component.html',
   styleUrls: ['./security.component.scss']
})
export class SecurityComponent implements OnInit, OnDestroy {
   // MFA related properties
   mfaEnabled: boolean = false;
   emailEnabled: boolean = false;
   totpEnabled: boolean = false;
   totpSetupInProgress: boolean = false;
   showQrCode: boolean = false;
   qrCodeUrl: string = '';
   verificationCode: string = '';
   showBackupCodes: boolean = false;
   backupCodes: string[] = [];
   mfaSecret: string = '';
   backupCodesTimer: any; // Timer for auto-closing backup codes
   backupCodesCountdown: number = 300; // 5 minutes in seconds
   countdownTimer: any; // Timer for countdown display

   constructor(
      private http: HttpClient,
      private toastrService: ToastrService,
      public translate: TranslateService
   ) { }

   ngOnInit(): void {
      // Load current MFA settings
      this.loadMfaSettings();
   }

   // Load current MFA settings
   loadMfaSettings() {
      const token = JSON.parse(localStorage.getItem('ACCESS_TOKEN'));

      this.http.get(`${environment.apiUrl}/mfa/status`, {
         headers: {
            Authorization: `Bearer ${token}`
         }
      }).subscribe(
         (response: any) => {
            this.mfaEnabled = response.enabled;
            this.emailEnabled = response.emailEnabled || false;
            this.totpEnabled = response.totpEnabled || false;

            // Check if TOTP setup is in progress (has method/secret but not enabled)
            if (!response.enabled && response.method === 'TOTP' && response.hasSecret) {
               this.totpSetupInProgress = true;
            } else {
               this.totpSetupInProgress = false;
            }
         },
         error => {
            console.log('Could not load MFA settings');
         }
      );
   }

   // Toggle MFA on/off
   toggleMfaStatus(event: any) {
      const enabled = event.target.checked;

      if (enabled) {
         // Enable MFA with EMAIL by default (primary method)
         this.setupEmailMfa();
      } else {
         // Disable all MFA
         this.disableAllMfaSwal();
      }
   }

   // Toggle Email MFA
   toggleEmailMfa(event: any) {
      const enabled = event.target.checked;

      if (enabled) {
         this.setupEmailMfa();
      } else {
         this.disableEmailMfa();
      }
   }

   // Toggle TOTP MFA
   toggleTotpMfa(event: any) {
      const enabled = event.target.checked;

      if (enabled) {
         this.setupTotpMfa();
      } else {
         // If setup is in progress, cancel it
         if (this.totpSetupInProgress) {
            this.cancelTotpSetup();
         } else {
            // If actually enabled, disable it
            this.disableTotpMfa();
         }
      }
   }

   // Setup Email MFA
   setupEmailMfa() {
      const token = JSON.parse(localStorage.getItem('ACCESS_TOKEN'));

      this.http.post(`${environment.apiUrl}/mfa/setup`, { method: 'EMAIL' }, {
         headers: {
            Authorization: `Bearer ${token}`
         }
      }).subscribe(
         (response: any) => {
            this.mfaEnabled = true;
            this.emailEnabled = true;

            // Only show backup codes if they're included in response (new setup)
            if (response.backupCodes) {
               this.showBackupCodes = true;
               this.backupCodes = response.backupCodes;
               this.startBackupCodesTimer();
            }

            this.updateUserMfaSettings();
            this.toastrService.success(this.translate.instant('new_entries.email_verification_enablet_successfully'));
         },
         error => {
            this.emailEnabled = false;
            this.toastrService.error('Failed to setup email verification');
         }
      );
   }

   // Setup TOTP MFA
   setupTotpMfa() {
      const token = JSON.parse(localStorage.getItem('ACCESS_TOKEN'));

      // Don't immediately set totpEnabled to true - wait for verification
      this.totpSetupInProgress = true;

      this.http.post(`${environment.apiUrl}/mfa/setup`, { method: 'TOTP' }, {
         headers: {
            Authorization: `Bearer ${token}`
         }
      }).subscribe(
         (response: any) => {
            this.showQrCode = true;
            this.qrCodeUrl = response.qrCode;
            this.mfaSecret = response.secret;
            this.toastrService.info(this.translate.instant('new_entries.please_scan_the_qr_code_with_your_authenticator_app'));
         },
         error => {
            this.totpSetupInProgress = false;
            this.totpEnabled = false; // Reset checkbox
            this.toastrService.error('Failed to setup authenticator app');
         }
      );
   }

   // Verify TOTP Setup
   verifyTotpSetup() {
      if (!this.verificationCode || this.verificationCode.length !== 6) {
         this.toastrService.warning('Please enter a valid 6-digit verification code');
         return;
      }

      const token = JSON.parse(localStorage.getItem('ACCESS_TOKEN'));

      this.http.post(`${environment.apiUrl}/mfa/verify-setup`, { code: this.verificationCode }, {
         headers: {
            Authorization: `Bearer ${token}`
         }
      }).subscribe(
         (response: any) => {
            // Now we can actually enable TOTP
            this.mfaEnabled = true;
            this.totpEnabled = true;
            this.totpSetupInProgress = false;
            this.showQrCode = false;

            // Always show backup codes after TOTP verification (new setup)
            this.showBackupCodes = true;
            this.backupCodes = response.backupCodes;
            this.startBackupCodesTimer();

            this.verificationCode = '';
            this.updateUserMfaSettings();
            this.toastrService.success('Authenticator app enabled successfully');
         },
         error => {
            // Don't change totpEnabled state on verification failure
            this.toastrService.error('Invalid verification code. Please try again.');
         }
      );
   }

   // Disable Email MFA
   disableEmailMfa() {
      // If this would disable all MFA (TOTP is not enabled), show confirmation
      if (!this.totpEnabled) {
         this.disableAllMfaSwal();
         return;
      }

      const token = JSON.parse(localStorage.getItem('ACCESS_TOKEN'));

      this.http.post(`${environment.apiUrl}/mfa/disable`, { method: 'EMAIL' }, {
         headers: {
            Authorization: `Bearer ${token}`
         }
      }).subscribe(
         (response: any) => {
            this.emailEnabled = false;
            this.updateUserMfaSettings();
            this.toastrService.success(this.translate.instant('notifications.email_verification_disabled'));
         },
         error => {
            this.emailEnabled = true; // Revert on error
            this.toastrService.error('Failed to disable email verification');
         }
      );
   }

   // Disable TOTP MFA
   disableTotpMfa() {
      // If this would disable all MFA (Email is not enabled), show confirmation
      if (!this.emailEnabled) {
         this.disableAllMfaSwal();
         return;
      }

      const token = JSON.parse(localStorage.getItem('ACCESS_TOKEN'));

      this.http.post(`${environment.apiUrl}/mfa/disable`, { method: 'TOTP' }, {
         headers: {
            Authorization: `Bearer ${token}`
         }
      }).subscribe(
         (response: any) => {
            this.totpEnabled = false;
            this.updateUserMfaSettings();
            this.toastrService.success('Authenticator app disabled');
         },
         error => {
            this.totpEnabled = true; // Revert on error
            this.toastrService.error('Failed to disable authenticator app');
         }
      );
   }

   // Disable all MFA
   disableAllMfa() {
      const token = JSON.parse(localStorage.getItem('ACCESS_TOKEN'));

      this.http.post(`${environment.apiUrl}/mfa/disable`, { method: 'ALL' }, {
         headers: {
            Authorization: `Bearer ${token}`
         }
      }).subscribe(
         (response: any) => {
            this.mfaEnabled = false;
            this.emailEnabled = false;
            this.totpEnabled = false;
            this.showBackupCodes = false;
            this.updateUserMfaSettings();
            this.toastrService.success(this.translate.instant('new_entries.two_factor_suthentication_disablet'));
         },
         error => {
            this.mfaEnabled = true; // Revert on error
            this.toastrService.error('Failed to disable two-factor authentication');
         }
      );
   }

   // Cancel TOTP setup
   cancelTotpSetup() {
      // Clean up backend state - remove the stored secret and method
      const token = JSON.parse(localStorage.getItem('ACCESS_TOKEN'));

      this.http.post(`${environment.apiUrl}/mfa/cancel-setup`, {}, {
         headers: {
            Authorization: `Bearer ${token}`
         }
      }).subscribe(
         (response: any) => {
            // Success - backend cleaned up
         },
         error => {
            console.log('Failed to clean up backend state');
            // Handle backend error messages with translation keys
            if (error.error && error.error.messageKey) {
               this.toastrService.error(this.translate.instant('new_entries.' + error.error.messageKey));
            } else if (error.error && error.error.message) {
               this.toastrService.error(error.error.message);
            }
         }
      );

      // Reset frontend state
      this.showQrCode = false;
      this.verificationCode = '';
      this.totpEnabled = false;
      this.totpSetupInProgress = false;
      this.qrCodeUrl = '';
      this.mfaSecret = '';
   }

   // Copy secret to clipboard
   copySecret() {
      navigator.clipboard.writeText(this.mfaSecret).then(() => {
         this.toastrService.success(this.translate.instant('notifications.secret_code_copied_to_clipboard'));
      });
   }

   // Copy backup code to clipboard
   copyBackupCode(code: string) {
      navigator.clipboard.writeText(code).then(() => {
         this.toastrService.success('Backup code copied to clipboard');
      });
   }

   // Handle verification code input
   onVerificationCodeInput(event: any) {
      // Only allow numbers
      const value = event.target.value.replace(/[^0-9]/g, '');
      this.verificationCode = value;
   }

   // Download backup codes
   downloadBackupCodes() {
      const codesText = this.backupCodes.join('\n');
      const blob = new Blob([codesText], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'backup-codes.txt';
      link.click();
      window.URL.revokeObjectURL(url);
   }

   // Regenerate backup codes
   async regenerateBackupCodes() {
      const result = await Swal.fire({
         title: this.translate.instant('new_entries.generate_new_backup_codes'),
         text: this.translate.instant('new_entries.this_will_invalidate_all_existing_backup_codes'),
         icon: 'warning',
         showCancelButton: true,
         confirmButtonColor: '#3085d6',
         cancelButtonColor: '#b5adad',
         confirmButtonText: this.translate.instant('new_entries.generate_new_codes'),
         cancelButtonText: this.translate.instant('new_entries.cancel'),
      });

      if (result.isConfirmed) {
         const token = JSON.parse(localStorage.getItem('ACCESS_TOKEN'));

         this.http.post(`${environment.apiUrl}/mfa/regenerate-backup-codes`, {}, {
            headers: {
               Authorization: `Bearer ${token}`
            }
         }).subscribe(
            (response: any) => {
               this.backupCodes = response.backupCodes;
               this.showBackupCodes = true;
               this.startBackupCodesTimer();
               this.toastrService.success(this.translate.instant('notifications.new_backup_codes_generated'));
            },
            error => {
               this.toastrService.error('Failed to generate new backup codes');
            }
         );
      }
   }

   // Close backup codes section
   closeBackupCodes() {
      this.showBackupCodes = false;
      this.clearBackupCodesTimer();
   }

   // Start timer to auto-close backup codes after 5 minutes
   startBackupCodesTimer() {
      this.clearBackupCodesTimer(); // Clear any existing timer
      this.backupCodesCountdown = 300; // Reset to 5 minutes

      // Start countdown display timer
      this.countdownTimer = setInterval(() => {
         this.backupCodesCountdown--;
         if (this.backupCodesCountdown <= 0) {
            this.clearBackupCodesTimer();
         }
      }, 1000);

      // Auto-close timer
      this.backupCodesTimer = setTimeout(() => {
         this.showBackupCodes = false;
         this.toastrService.info('Backup codes section auto-closed. You can regenerate them anytime from settings.');
      }, 5 * 60 * 1000); // 5 minutes
   }

   // Clear the backup codes timer
   clearBackupCodesTimer() {
      if (this.backupCodesTimer) {
         clearTimeout(this.backupCodesTimer);
         this.backupCodesTimer = null;
      }
      if (this.countdownTimer) {
         clearInterval(this.countdownTimer);
         this.countdownTimer = null;
      }
   }

   // Format countdown time for display
   getCountdownDisplay(): string {
      const minutes = Math.floor(this.backupCodesCountdown / 60);
      const seconds = this.backupCodesCountdown % 60;
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
   }

   // Clean up timer when component is destroyed
   ngOnDestroy() {
      this.clearBackupCodesTimer();
   }

   // Update user MFA settings in localStorage
   private updateUserMfaSettings() {
      const user = JSON.parse(localStorage.getItem('USER'));
      if (user) {
         user.mfaEnabled = this.mfaEnabled;

         // Determine the correct method based on enabled states
         if (this.emailEnabled && this.totpEnabled) {
            user.mfaMethod = 'BOTH';
         } else if (this.emailEnabled) {
            user.mfaMethod = 'EMAIL';
         } else if (this.totpEnabled) {
            user.mfaMethod = 'TOTP';
         } else {
            user.mfaMethod = null;
         }

         localStorage.setItem('USER', JSON.stringify(user));
      }
   }

   async disableAllMfaSwal() {
      const result = await Swal.fire({
         title: this.translate.instant('new_entries.disable_two_factor_authentication'),
         text: this.translate.instant('new_entries.this_will_remove_all_authentication_methods'),
         icon: 'warning',
         showCancelButton: true,
         confirmButtonColor: '#F44336',
         cancelButtonColor: '#b5adad',
         confirmButtonText: this.translate.instant('new_entries.yes_disable_it'),
         cancelButtonText: this.translate.instant('new_entries.cancel'),
      });

      if (result.isConfirmed) {
         this.disableAllMfa();
      }
   }
}
